import React, { useState, useEffect, useCallback } from "react";
import { IoSaveOutline } from "react-icons/io5";
import { FiX, FiChevronRight } from "react-icons/fi";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BeatLoader } from "react-spinners";
import supabase from "../../supabase";
import TopBar from "../../components/TopBar";
import { motion, AnimatePresence } from "framer-motion";

const CATEGORY_DISPLAY_NAMES = {
  'Crocs': 'Sandals',
};

const sortSizes = (sizes) => {
  return [...sizes].sort((a, b) => {
    const numA = parseFloat(a);
    const numB = parseFloat(b);

    if (!isNaN(numA) && !isNaN(numB)) {
      return numA - numB;
    }

    if (!isNaN(numA)) return -1;
    if (!isNaN(numB)) return 1;

    const sizeOrder = ["XS", "S", "M", "L", "XL", "XXL", "XXXL", "3XL", "4XL"];
    const indexA = sizeOrder.indexOf(a.toUpperCase());
    const indexB = sizeOrder.indexOf(b.toUpperCase());

    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;

    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  });
};

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [editData, setEditData] = useState({
    sizesList: [],
    brandsList: []
  });

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    let { data, error } = await supabase
      .from("categories")
      .select("id, category, thumbnail, sizes, brands")
      .order("category", { ascending: true });
    
    if (error) {
      console.error("Error fetching categories: ", error);
      toast.error("Error fetching categories");
    } else {
      setCategories(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleOpenEditModal = (cat) => {
    setCurrentCategory(cat);
    setEditData({
      sizesList: Array.isArray(cat.sizes) ? sortSizes(cat.sizes) : [],
      brandsList: Array.isArray(cat.brands) ? cat.brands : []
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!currentCategory) return;

    setLoading(true);
    
    const { data, error, status } = await supabase
      .from("categories")
      .update({
        sizes: editData.sizesList,
        brands: editData.brandsList
      })
      .eq("id", currentCategory.id)
      .select();

    // Update attempt: logging removed for production

    if (error) {
      console.error("Supabase Error:", error);
      toast.error(`Error: ${error.message || "Failed to update"}`);
    } else if (!data || data.length === 0) {
      console.warn("No rows updated. Check RLS policies or record ID.");
      toast.warning("No changes were applied. You might not have permission to edit this record.");
    } else {
      toast.success("Category updated successfully");
      setCategories(categories.map(c => 
        c.id === currentCategory.id 
          ? { ...c, sizes: editData.sizesList, brands: editData.brandsList } 
          : c
      ));
      setModalOpen(false);
    }
    setLoading(false);
  };

  return (
    <div className="page min-h-screen bg-[#f8fafc] text-slate-900 font-sans">
      <TopBar />
      
      <main className="max-w-6xl mx-auto p-6 md:p-10">
        <header className="mb-10">
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-800">
            Edit Categories
          </h1>
          <p className="text-slate-500 mt-2">Update sizes and brands for your product categories</p>
        </header>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.length > 0 ? (
            categories.map((cat, index) => (
              <motion.div
                key={cat.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ y: -5 }}
                className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 overflow-hidden border border-slate-100 group cursor-pointer"
                onClick={() => handleOpenEditModal(cat)}
              >
                <div className="relative h-48 overflow-hidden bg-slate-100">
                  <img 
                    src={cat.thumbnail || "https://via.placeholder.com/400x300?text=No+Image"} 
                    alt={cat.category}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                    <span className="text-white font-medium flex items-center">
                      Edit Category <FiChevronRight className="ml-1" />
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-slate-800 mb-2">
                    {CATEGORY_DISPLAY_NAMES[cat.category] || cat.category}
                  </h3>
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-1">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider w-full mb-1">Sizes:</span>
                      {cat.sizes && cat.sizes.length > 0 ? (
                        cat.sizes.slice(0, 5).map((s, i) => (
                          <span key={i} className="px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded text-xs font-semibold">
                            {s}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-slate-400 italic">No sizes defined</span>
                      )}
                      {cat.sizes && cat.sizes.length > 5 && (
                        <span className="text-xs text-slate-400">+{cat.sizes.length - 5} more</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider w-full mb-1">Brands:</span>
                      {cat.brands && cat.brands.length > 0 ? (
                        cat.brands.slice(0, 3).map((b, i) => (
                          <span key={i} className="px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded text-xs font-semibold">
                            {b}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-slate-400 italic">No brands defined</span>
                      )}
                      {cat.brands && cat.brands.length > 3 && (
                        <span className="text-xs text-slate-400">+{cat.brands.length - 3} more</span>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="col-span-full py-20 text-center">
              <BeatLoader color="#4F46E5" />
            </div>
          )}
        </div>
      </main>

      <AnimatePresence>
        {modalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-md"
              onClick={() => setModalOpen(false)}
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative bg-white rounded-3xl shadow-2xl overflow-hidden w-full max-w-2xl border border-slate-100 flex flex-col md:flex-row"
            >
              <div className="md:w-1/3 bg-slate-100 h-48 md:h-auto">
                <img 
                  src={currentCategory?.thumbnail} 
                  alt={currentCategory?.category}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8 md:w-2/3">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800">
                      {CATEGORY_DISPLAY_NAMES[currentCategory?.category] || currentCategory?.category}
                    </h2>
                    <p className="text-slate-500 text-sm">Update category specifications</p>
                  </div>
                  <button 
                    onClick={() => setModalOpen(false)}
                    className="p-2 hover:bg-slate-100 rounded-full text-slate-400 transition-colors"
                  >
                    <FiX size={24} />
                  </button>
                </div>
                
                <div className="space-y-6">
                  {/* Sizes Section */}
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">Sizes</label>
                    <div className="flex flex-wrap gap-2 p-3 bg-slate-50 border border-slate-200 rounded-xl min-h-[50px]">
                      {editData.sizesList.map((size, index) => (
                        <motion.span 
                          layout
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          key={`${size}-${index}`}
                          className="flex items-center gap-1 px-3 py-1 bg-indigo-600 text-white rounded-lg text-sm font-semibold group"
                        >
                          {size}
                          <button 
                            onClick={() => {
                              setEditData(prev => ({
                                ...prev,
                                sizesList: prev.sizesList.filter((_, i) => i !== index)
                              }));
                            }}
                            className="hover:bg-indigo-500 rounded-full p-0.5 transition-colors"
                          >
                            <FiX size={14} />
                          </button>
                        </motion.span>
                      ))}
                      <input
                        type="text"
                        className="bg-transparent outline-none flex-1 min-w-[100px] text-sm py-1"
                        placeholder="Type size and press Enter..."
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const val = e.target.value.trim();
                            if (val && !editData.sizesList.includes(val)) {
                              setEditData(prev => ({
                                ...prev,
                                sizesList: sortSizes([...prev.sizesList, val])
                              }));
                              e.target.value = '';
                            }
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* Brands Section */}
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">Brands</label>
                    <div className="flex flex-wrap gap-2 p-3 bg-slate-50 border border-slate-200 rounded-xl min-h-[50px]">
                      {editData.brandsList.map((brand, index) => (
                        <motion.span 
                          layout
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          key={`${brand}-${index}`}
                          className="flex items-center gap-1 px-3 py-1 bg-emerald-600 text-white rounded-lg text-sm font-semibold"
                        >
                          {brand}
                          <button 
                            onClick={() => {
                              setEditData(prev => ({
                                ...prev,
                                brandsList: prev.brandsList.filter((_, i) => i !== index)
                              }));
                            }}
                            className="hover:bg-emerald-500 rounded-full p-0.5 transition-colors"
                          >
                            <FiX size={14} />
                          </button>
                        </motion.span>
                      ))}
                      <input
                        type="text"
                        className="bg-transparent outline-none flex-1 min-w-[100px] text-sm py-1"
                        placeholder="Type brand and press Enter..."
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const val = e.target.value.trim();
                            if (val && !editData.brandsList.includes(val)) {
                              setEditData(prev => ({
                                ...prev,
                                brandsList: [...prev.brandsList, val]
                              }));
                              e.target.value = '';
                            }
                          }
                        }}
                      />
                    </div>
                  </div>

                  <div className="flex gap-4 pt-4">
                    <button
                      className="flex-1 px-6 py-3.5 bg-slate-100 text-slate-600 font-bold rounded-xl hover:bg-slate-200 transition-colors"
                      onClick={() => setModalOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="flex-1 px-6 py-3.5 bg-indigo-600 text-white font-bold rounded-xl shadow-lg shadow-indigo-200 hover:bg-indigo-700 transition-all duration-300 flex items-center justify-center gap-2"
                      onClick={handleSave}
                    >
                      <IoSaveOutline size={20} />
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {loading && (
        <div className="fixed inset-0 flex items-center justify-center z-[70] bg-white/40 backdrop-blur-[2px]">
          <div className="bg-white p-6 rounded-2xl shadow-xl border border-slate-100">
            <BeatLoader color="#4F46E5" />
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;
